/**
 * Firestore-backed RTK Query API for the parking reservations view.
 *
 * Replaces the REST polling in Parking.tsx (useGetParkingSpotsQuery)
 * with a live onSnapshot listener.
 *
 * Firestore path (Kotlin server side):
 *   Kantoordag/kd{yyyyMMdd}/ReservationsView/{building}/Reservations/{userId}
 *
 * One document per user who requested a parking spot. No defaults needed —
 * an empty collection simply means no parking requests for that day.
 */

import { collection, orderBy, query, CollectionReference, Firestore, QuerySnapshot } from 'firebase/firestore';
import { Periode, ReservationView } from '../kantoordagApi';
import { createFirestoreStreamApi } from '../firestoreModel';
import { kantoordagDateKey, toIsoDate } from './firestoreUtils';

type RawDoc = Record<string, unknown>;

function toIsoDateTime(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    const ts = value as { toDate?: () => Date; seconds?: number };
    if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000).toISOString();
    return '';
}

function mapReservationView(raw: RawDoc): ReservationView {
    return {
        date: toIsoDate(raw.date),
        building: raw.building as string ?? '',
        userId: raw.userId as string ?? '',
        userName: raw.userName as string ?? '',
        reservationDate: toIsoDateTime(raw.reservationDate),
        reserved: (raw.reserved as boolean) ?? false,
        periode: (raw.periode as string ?? 'HELE_DAG') as Periode,
    };
}

function mapData(snapshot: QuerySnapshot): ReservationView[] {
    const results: ReservationView[] = [];
    snapshot.forEach(doc => results.push(mapReservationView(doc.data() as RawDoc)));
    return results;
}

type ParkingViewArg = { date: string; building: string };

export const kantoordagParkingViewStreamingApi = createFirestoreStreamApi<ReservationView[], ParkingViewArg>({
    reducerPath: 'kantoordagParkingViewApi',

    getRef: (db: Firestore, { date, building }: ParkingViewArg) =>
        query(
            collection(db, 'Kantoordag', kantoordagDateKey(date), 'ReservationsView', building, 'Reservations') as CollectionReference,
            orderBy('reservationDate', 'asc'),
        ),

    mapData,
    emptyResult: () => [],
});

export const useGetParkingViewQuery = kantoordagParkingViewStreamingApi.endpoints.get.useQuery;
